import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isLoggedIn } from "api/coachAuth";
import type { CustomResponseFormat } from "api/instance";
import { taskApi } from "api";
import { automaticThoughtIdType } from "api/conceptApi";
import { DASHBOARD_DETAIL_ENDPOINT, DASHBOARD_DOMAIN } from "api/dashboardApi";
import { HOME_DOMAIN, HOME_MEDITATION_LIST_ENDPOINT } from "api/homeApi";
import {
  MEDITATION_RECORD_DOMAIN,
  MEDITATION_RECORD_LIST_ENDPOINT,
  MEDITATION_RECORD_STATUS_DETAIL_ENDPOINT,
  meditationRecordApi,
} from "api/meditationRecordApi";
import { selfCheckApi } from "api/selfCheckApi";
import {
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_LIST_ENDPOINT,
  THOUGHTRECORD_STATUS_DETAIL_ENDPOINT,
  thoughtRecordApi,
} from "api/thoughtRecordApi";
import { ProgramContentType } from "data/BlockComponent";
import { MultipleSelectionType, PercentageType } from "data/CellComponent";
import useRefetchTaskList from "hooks/useRefetchTaskList";
import useOptimisticHomeListDone from "hooks/useOptimisticHomeListDone";
import { applyDoneToStatus } from "hooks/applyDoneToStatus";
import useSaveMeditationRecordWithRawContent from "hooks/useSaveMeditationRecordWithRawContent";
import useOpenMeditationRecord from "hooks/useOpenMeditationRecord";
import useSaveThoughtRecordWithRawContent from "hooks/useSaveThoughtRecordWithRawContent";
import useSubmitImpactScore from "hooks/useSubmitImpactScore";
import {
  getReferenceData,
  getSelfCheckScore,
  getTaskIdFromTaskKey,
  isAllUserFieldFilled,
  isBlockUserFieldFilled,
  isLineHidden,
} from "logic/logics";
import React, { useCallback, useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { taskListAtom } from "recoil/taskListAtom";
import { userAtom } from "recoil/userAtom";
import { doneButtonLoadingAtom } from "recoil/doneButtonLoadingAtom";
import useRemoveTab from "hooks/useRemoveTab";
import { invalidTextareaIndexAtom } from "recoil/invalidTextareaIndexAtom";
import { Box, Button, Stack } from "@mui/joy";
import CustomModal from "../../common/CustomModal";
import useIsInappWebview from "hooks/useIsInappWebview";
import useDetectIOS from "hooks/useDetectIOS";
import { CheckIconSvgDoneButton } from "../../../static/image/common/SvgAssets";
import { getDoneModalContent } from "../../../utils/modalUtil";
import { homeThoughtRecordListAtom } from "../../../recoil/homeThoughtRecordListAtom";
import { findMostRecentDoneAt } from "../../../utils/findMostRecentDoneAt";
import { theme } from "../../../styles/theme";
import CustomTypography from "../../common/CustomTypography";
import { useNavigate } from "react-router-dom";
import useOpenThoughtRecord from "../../../hooks/useOpenThoughtRecord";
import { patientAtom } from "../../../recoil/patientAtom";
import { captureException } from "../../../utils/sentry";
import useOpenTask from "../../../hooks/useOpenTask";
import useGetTaskTitleFromTaskKey from "hooks/useGetTaskTitleFromTaskKey";
import useUserSimComplete from "hooks/useUserSimComplete";

const USER_SIM_COMPLETE_WAIT_MS = 3000;

export default function DoneButton({
  taskKey,
  thoughtRecordKey,
  activityRecordKey,
  meditationRecordKey,
  data,
  doneWithEnter,
  setDoneWithEnter,
  setMoveToIndex,
  isDone,
  isOpen,
  size,
  translationVersion,
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  activityRecordKey?: string;
  meditationRecordKey?: string;
  data?: ProgramContentType[];
  doneWithEnter?: boolean;
  setDoneWithEnter?: React.Dispatch<React.SetStateAction<boolean>>;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
  isDone?: boolean;
  isOpen?: boolean;
  size?: "sm" | "lg";
  translationVersion?: string;
}) {
  const isFirstDone = data?.every((element) => !element.indentation);
  const isSelfCheck =
    getTaskIdFromTaskKey(`${taskKey}`) === "0-1-A" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "0-2-A" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "2-3" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "2-4" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "102-1-A" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "102-2-A";

  const isThoughtRecord =
    taskKey?.includes("2-2-T") && thoughtRecordKey !== undefined;
  const isMeditationRecord =
    taskKey?.includes("5-7-T") && meditationRecordKey !== undefined;

  const [taskListState, setTaskListState] = useRecoilState(taskListAtom);
  const homeThoughtRecordList = useRecoilValue(homeThoughtRecordListAtom);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useRecoilState(doneButtonLoadingAtom);

  const setInvalidTextareaIndexList = useSetRecoilState(
    invalidTextareaIndexAtom
  );

  const [doneModalContent, setDoneModalContent] = useState({
    title: "",
    subtitle: "",
    key: "",
  });

  const isInappWebview = useIsInappWebview();
  const { isIOS } = useDetectIOS();

  const focusElement = useCallback(
    (el: HTMLElement) => {
      el.focus();
      try {
        const input = el as HTMLInputElement | HTMLTextAreaElement;
        const len = input.value?.length || 0;
        input.setSelectionRange?.(len, len);
      } catch {}

      if (isIOS) {
        try {
          el.click();
        } catch {}
      }
    },
    [isIOS]
  );

  const taskId = getTaskIdFromTaskKey(taskKey);
  const title = useGetTaskTitleFromTaskKey(taskKey);
  const notifyUserSimComplete = useUserSimComplete({
    taskKey,
    title,
    translationVersion,
    thoughtRecordKey,
  });
  // 모의 내담자는 마치기 알림을 보낸 뒤(최대 USER_SIM_COMPLETE_WAIT_MS) 다음 단계로 간다.
  // 인앱에서 화면이 닫히면(navigate_back) WebView 가 파괴되어 알림이 사라지기 때문. 그 밖의 사용자는 바로 진행.
  const afterUserSimComplete = (next: () => void) => {
    const sending = notifyUserSimComplete(data);
    if (!sending) return next();
    Promise.race([
      sending,
      new Promise((resolve) => setTimeout(resolve, USER_SIM_COMPLETE_WAIT_MS)),
    ]).then(next);
  };
  const automaticThoughtId = taskId.includes("_")
    ? (taskId.split("_")[1] as automaticThoughtIdType)
    : undefined;

  const user = useRecoilValue(userAtom);
  const patient = useRecoilValue(patientAtom);

  const isPatient = user?.role === "patient";
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isOptimisticDone, setIsOptimisticDone] = useState(false);

  const { removeTab } = useRemoveTab();

  // const disabled =
  //   isDone === undefined ||
  //   !isAllUserFieldFilled(data, false, user) ||
  //   (isPatient && isDone && !isOpen && !isMeditationRecord && !isThoughtRecord);

  useEffect(() => {
    setDisabled(
      isDone === undefined ||
        !isAllUserFieldFilled(data, false, user) ||
        (isPatient &&
          isDone &&
          !isOpen &&
          !isMeditationRecord &&
          !isThoughtRecord)
    );
  }, [
    isDone,
    isOpen,
    isMeditationRecord,
    isThoughtRecord,
    data,
    user,
    isPatient,
  ]);

  const handleGoBack = () => {
    if (isInappWebview) {
      removeTab();
    }
  };

  const allowedOpenTaskIdsForThoughtRecord = new Set([
    "0-7-A",
    "7-2-A",
    "9-5-A",
    "9-7-A",
    "9-9-A",
    "9-11-A",
  ]);

  const isLastDone = isThoughtRecord
    ? // Original condition: all other open thought records are done AND all non-optional tasks are done
      (homeThoughtRecordList
        .filter(
          (element) =>
            element.thoughtRecordKey !== thoughtRecordKey && element.isOpen
        )
        .every((element) => element.isDone) &&
        taskListState
          .filter((element) => !element.isOptional)
          .every((element) => element.isDone)) ||
      // New allowance: no other remaining pending thought records AND only whitelisted tasks remain open/not done
      (homeThoughtRecordList
        .filter((element) => element.thoughtRecordKey !== thoughtRecordKey)
        .every((element) => !element.isOpen || element.isDone) &&
        taskListState
          .filter((element) => !element.isOptional)
          .every((element) => {
            if (element.isDone) return true;
            const id = getTaskIdFromTaskKey(element.taskKey);
            return element.isOpen && allowedOpenTaskIdsForThoughtRecord.has(id);
          }))
    : taskListState
        .filter((element) => element.taskKey !== taskKey)
        .filter((element) => !element.isOptional)
        .every((element) => element.isDone) &&
      homeThoughtRecordList
        .filter((element) => element.isOpen)
        .every((element) => element.isDone);

  const queryClient = useQueryClient();

  const saveSelfCheckQueryFn = useCallback(
    () => selfCheckApi.save(getSelfCheckScore(data)),
    [data]
  );

  const highlightUnfilledBlocks = () => {
    if (disabled && data) {
      const textareaIndices: string[] = [];
      const isCoach = isLoggedIn(user) && !isPatient;

      data.forEach((block, bIdx) => {
        const isBlockHidden =
          block.isHidden ||
          block.isAlwaysHidden ||
          (isCoach && !block.isShown && block.hideIfCoach) ||
          (!isCoach && (!block.isShown || block.hideIfPatient));

        if (!isBlockHidden && !isBlockUserFieldFilled(block, data, user)) {
          block.lines.forEach((line, lIdx) => {
            if (!isLineHidden(data, block, line)) {
              line.forEach((cell, cIdx) => {
                if (
                  cell.type === "textarea" &&
                  !((cell.content as any)?.value || "").trim()
                ) {
                  textareaIndices.push(`${bIdx}-${lIdx}-${cIdx}`);
                }

                if (
                  cell.type === "feedback" &&
                  ((cell.content as any)?.value || "").trim().length > 0
                ) {
                  textareaIndices.push(`${bIdx}-${lIdx}-${cIdx}`);
                }

                if (
                  cell.type === "percentage" &&
                  !(
                    (cell.content as any)?.value &&
                    (!(cell.content as any)?.warnIfZero ||
                      Number((cell.content as any)?.value) > 0)
                  )
                ) {
                  textareaIndices.push(`${bIdx}-${lIdx}-${cIdx}`);
                }

                if (cell.type === "buttongroup_multipleChoice") {
                  const content = cell.content as MultipleSelectionType;
                  const selectedCount = (content?.selectedIndices || []).length;
                  const required = content?.requiredSelectionCount;
                  const invalid =
                    selectedCount === 0 ||
                    (required !== undefined && selectedCount !== required);
                  if (invalid) {
                    textareaIndices.push(`${bIdx}-${lIdx}-${cIdx}`);
                  }
                }
              });
            }
          });
        }
      });
      setInvalidTextareaIndexList(textareaIndices);

      if (textareaIndices.length > 0) {
        const [first] = textareaIndices;
        const blockIndex = Number(first.split("-")[0]);
        if (setMoveToIndex) {
          setMoveToIndex(blockIndex);
        }

        const el = document.getElementById(`textarea-${first}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });

          const inputEl = el.querySelector(
            "textarea, input[type='text'], input[type='number'], input[type='tel'], .ql-editor"
          );

          focusElement((inputEl || el) as HTMLElement);
        }
      }
    }
  };

  const { mutate: saveSelfCheckData, isLoading: isSelfCheckLoading } =
    useMutation(saveSelfCheckQueryFn, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          DASHBOARD_DOMAIN,
          DASHBOARD_DETAIL_ENDPOINT,
        ]);
      },
    });

  const trPercentage =
    (getReferenceData(data || [], "percentageThought") as PercentageType)
      ?.percentage || 0;
  const trDate = getReferenceData(data || [], "date")?.value || "";
  const trSituation = getReferenceData(data || [], "situation")?.value || "";
  const trSituationIndirect =
    getReferenceData(data || [], "situation_indirect")?.value || "";
  const trSituationDirect =
    getReferenceData(data || [], "situation_direct")?.value || "";

  const {
    save: saveNewThoughtRecordDataWithRawRecord,
    thoughtRecordKey: newThoughtRecordKey,
  } = useSaveThoughtRecordWithRawContent({
    percentageThought: trPercentage,
    date: trDate,
    situation: trSituation,
    situation_indirect: trSituationIndirect,
    situation_direct: trSituationDirect,
  });

  const meditationType =
    getReferenceData(data || [], "meditation_type")?.value || "";
  const {
    save: saveNewMeditationRecord,
    meditationRecordKey: newMeditationRecordKey,
  } = useSaveMeditationRecordWithRawContent({
    type: meditationType,
    groupType: "coping",
  });

  const openMeditationRecord = useOpenMeditationRecord(
    newMeditationRecordKey,
    "coping"
  );

  const openMeditationRecordDetailPage = useOpenTask({
    taskKey: "meditation-record-detail",
    meditationRecordKey: newMeditationRecordKey,
    params: { groupType: "coping" },
  });
  const openThoughtRecord = useOpenThoughtRecord(newThoughtRecordKey);

  const submit = useSubmitImpactScore({
    taskKey: taskKey,
    data: data || [],
    automaticThoughtId: automaticThoughtId,
  });

  const [isDoneModalOpen, setModalOpen] = useState<boolean>(false);
  const [isUndoneModalOpen, setUndoneModalOpen] = useState<boolean>(false);
  const [isUpdatePolicyModalOpen, setUpdatePolicyModalOpen] =
    useState<boolean>(false);
  const [isMoveToMeditationModalOpen, setMoveToMeditationModalOpen] =
    useState<boolean>(false);
  const [isMoveToThoughtRecordModalOpen, setMoveToThoughtRecordModalOpen] =
    useState<boolean>(false);

  const refetchTaskList = useRefetchTaskList();
  const { markTaskDone, markThoughtRecordDone, markMeditationRecordDone } =
    useOptimisticHomeListDone();

  // 활동지 done·기록 done/undone 의 read-after-write 지연 대비. 낙관적 캐시 반영 후 stale 윈도우가 지난 뒤
  // 1회 재검증. 즉시 invalidate 하면 stale 값을 받아와 덮어쓰므로 지연시킨다(10초 폴링이 최종 backstop).
  const REVALIDATE_DELAY_MS = 2000;

  const thoughtRecordStatusKey = [
    THOUGHTRECORD_DOMAIN,
    THOUGHTRECORD_STATUS_DETAIL_ENDPOINT,
    thoughtRecordKey,
  ];
  const meditationRecordStatusKey = [
    MEDITATION_RECORD_DOMAIN,
    MEDITATION_RECORD_STATUS_DETAIL_ENDPOINT,
    meditationRecordKey,
  ];

  // 기록 status/detail 캐시에 done/undone 을 선반영하고 재검증은 지연시킨다.
  // 즉시 재조회하면 리더 replica 의 stale isDone 이 상세 페이지의 자동 저장 게이트를 다시 열 수 있다.
  const applyRecordStatus = (queryKey: unknown[], isDone: boolean) => {
    queryClient.setQueryData<
      CustomResponseFormat<{ isDone: boolean; lastDoneAt?: string }>
    >(queryKey, (old) =>
      applyDoneToStatus(old, isDone, new Date().toISOString())
    );
    setTimeout(
      () => queryClient.invalidateQueries(queryKey),
      REVALIDATE_DELAY_MS
    );
  };

  // 홈 목록은 낙관 반영 뒤 REVALIDATE_DELAY_MS 지나서 재조회한다 (활동지 done 과 동일).
  const refetchTaskListAndOpenSidebar = () => {
    setTimeout(refetchTaskList, REVALIDATE_DELAY_MS);
    checkModalOpenAfterDone();
  };

  const shouldShowDoneModal = (modalKey: string): boolean => {
    const STORAGE_KEY = "doneModalShownToday";

    // 한국 시간 기준 오늘 날짜 (YYYY-MM-DD)
    const getKoreanDate = (): string => {
      const now = new Date();
      const koreanTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9
      return koreanTime.toISOString().split("T")[0];
    };

    const today = getKoreanDate();

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data = stored ? JSON.parse(stored) : { date: "", keys: [] };

      // 날짜가 바뀌면 초기화
      if (data.date !== today) {
        data.date = today;
        data.keys = [];
      }

      // 오늘 이미 본 모달인지 확인
      if (data.keys.includes(modalKey)) {
        return false;
      }

      // 오늘 본 모달 목록에 추가
      data.keys.push(modalKey);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

      return true;
    } catch (error) {
      console.error("Error managing done modal storage:", error);
      return true; // 에러 시 팝업은 보여줌
    }
  };

  const checkModalOpenAfterDone = () => {
    if (isFirstDone && isThoughtRecord && meditationType) {
      saveNewMeditationRecord();
      setMoveToMeditationModalOpen(true);
    } else if (
      isFirstDone &&
      isMeditationRecord &&
      trDate &&
      (trSituation || trSituationIndirect || trSituationDirect)
    ) {
      saveNewThoughtRecordDataWithRawRecord();
      setMoveToThoughtRecordModalOpen(true);
    } else if (
      isLastDone &&
      !isMeditationRecord &&
      !isMoveToMeditationModalOpen &&
      !isMoveToThoughtRecordModalOpen
    ) {
      if (shouldShowDoneModal(doneModalContent.key)) {
        setModalOpen(true);
      } else {
        handleGoBack();
      }
    } else {
      handleGoBack();
    }
  };

  useEffect(() => {
    if (isDone) {
      setIsOptimisticDone(false);
    }
  }, [isDone]);

  const { mutate: activityDone, isLoading: isActivityDoneLoading } =
    useMutation(
      async () => {
        if (data) {
          await taskApi.saveData(`${taskKey}`, {
            taskId: getTaskIdFromTaskKey(`${taskKey}`) || "",
            title: title,
            content: data || [],
            translationVersion: translationVersion,
          });
        }
        return taskApi.done(`${taskKey}`);
      },
      {
        onSuccess: () => {
          setIsOptimisticDone(true);
          markTaskDone(`${taskKey}`);
          afterUserSimComplete(checkModalOpenAfterDone);
          setTimeout(refetchTaskList, REVALIDATE_DELAY_MS);

          // queryClient.invalidateQueries([HOME_DOMAIN, HOME_TASK_LIST_ENDPOINT]);
          if (getTaskIdFromTaskKey(taskKey) === "8-6-A") {
            queryClient.invalidateQueries([
              HOME_DOMAIN,
              HOME_MEDITATION_LIST_ENDPOINT,
            ]);
          }
        },
        onError: (error) => {
          console.error("Activity done failed:", error);
          captureException(error, {
            tags: { action: "activity_done" },
            extra: { taskKey },
          });
          setIsLoading(false);
        },
      }
    );

  const { mutate: activityUndone } = useMutation(
    () => taskApi.undone(`${taskKey}`),
    {
      onSuccess: () => {
        setIsOptimisticDone(false);
        markTaskDone(`${taskKey}`, false);
        setTimeout(refetchTaskList, REVALIDATE_DELAY_MS);

        if (getTaskIdFromTaskKey(taskKey) === "8-6-A") {
          queryClient.invalidateQueries([
            HOME_DOMAIN,
            HOME_MEDITATION_LIST_ENDPOINT,
          ]);
        }

        setTaskListState((prev) =>
          prev.map((task) =>
            task.taskKey === taskKey
              ? { ...task, isDone: false, isOpen: true }
              : task
          )
        );
      },
    }
  );

  const { mutate: thoughtRecordDone, isLoading: isThoughtRecordDoneLoading } =
    useMutation(
      async () => {
        if (isThoughtRecord && data) {
          await thoughtRecordApi.saveData(
            `${thoughtRecordKey}`,
            patient?.patientId!!,
            {
              taskId: getTaskIdFromTaskKey(`${taskKey}`) || "",
              title: title,
              content: data || [],
              translationVersion: translationVersion,
              thoughtRecordKey: thoughtRecordKey,
            }
          );
        }
        return thoughtRecordApi.done(`${thoughtRecordKey}`);
      },
      {
        onSuccess: () => {
          setIsOptimisticDone(true);
          applyRecordStatus(thoughtRecordStatusKey, true);
          markThoughtRecordDone(`${thoughtRecordKey}`, true);
          queryClient.invalidateQueries([
            THOUGHTRECORD_DOMAIN,
            THOUGHTRECORD_LIST_ENDPOINT,
            patient?.patientId,
          ]);

          afterUserSimComplete(refetchTaskListAndOpenSidebar);
        },
        onError: (error) => {
          console.error("Thought record done failed:", error);
          captureException(error, {
            tags: { action: "thought_record_done" },
            extra: { thoughtRecordKey },
          });
          setIsLoading(false);
        },
      }
    );

  const { mutate: thoughtRecordUndone } = useMutation(
    () => thoughtRecordApi.undone(`${thoughtRecordKey}`),
    {
      onSuccess: () => {
        setIsOptimisticDone(false);
        queryClient.invalidateQueries([
          THOUGHTRECORD_DOMAIN,
          THOUGHTRECORD_LIST_ENDPOINT,
        ]);

        applyRecordStatus(thoughtRecordStatusKey, false);
        markThoughtRecordDone(`${thoughtRecordKey}`, false);

        setTimeout(refetchTaskList, REVALIDATE_DELAY_MS);
      },
    }
  );

  const {
    mutate: meditationRecordDone,
    isLoading: isMeditationRecordDoneLoading,
  } = useMutation(
    async () => {
      if (isMeditationRecord && data) {
        await meditationRecordApi.saveData(
          `${meditationRecordKey}`,
          patient?.patientId!!,
          {
            taskId: getTaskIdFromTaskKey(`${taskKey}`) || "",
            title: title,
            content: data || [],
            translationVersion: "v1",
          }
        );
      }
      return meditationRecordApi.done(`${meditationRecordKey}`);
    },
    {
      onSuccess: () => {
        setIsOptimisticDone(true);
        queryClient.invalidateQueries([
          MEDITATION_RECORD_DOMAIN,
          MEDITATION_RECORD_LIST_ENDPOINT,
        ]);

        applyRecordStatus(meditationRecordStatusKey, true);
        markMeditationRecordDone(`${meditationRecordKey}`, true);

        refetchTaskListAndOpenSidebar();
      },
      onError: (error) => {
        console.error("Meditation record done failed:", error);
        captureException(error, {
          tags: { action: "meditation_record_done" },
          extra: { meditationRecordKey },
        });
        setIsLoading(false);
      },
    }
  );

  const { mutate: meditationRecordUndone } = useMutation(
    () => meditationRecordApi.undone(`${meditationRecordKey}`),
    {
      onSuccess: () => {
        setIsOptimisticDone(false);
        queryClient.invalidateQueries([
          MEDITATION_RECORD_DOMAIN,
          MEDITATION_RECORD_LIST_ENDPOINT,
        ]);

        applyRecordStatus(meditationRecordStatusKey, false);
        markMeditationRecordDone(`${meditationRecordKey}`, false);

        setTimeout(refetchTaskList, REVALIDATE_DELAY_MS);
      },
    }
  );

  const undoneAction = () => {
    if (isThoughtRecord) {
      thoughtRecordUndone();
    } else if (isMeditationRecord) {
      meditationRecordUndone();
    } else {
      activityUndone();
    }
  };

  const doneAction = () => {
    if (data) {
      // Compute the most recent doneAt like NoticeBanner, and use it as the reference time
      const taskDoneAts = taskListState
        .filter((e) => e.isOpen && e.isDone && e.lastDoneAt)
        .map((e) => e.lastDoneAt!);
      const thoughtRecordDoneAts = homeThoughtRecordList
        .filter((e) => e.isOpen && e.isDone && e.lastDoneAt)
        .map((e) => e.lastDoneAt!);
      const mostRecentDoneAt = findMostRecentDoneAt(
        taskDoneAts,
        thoughtRecordDoneAts
      );

      const modalContent = getDoneModalContent({
        todayTaskUpdateCount: user?.todayTaskUpdateCount,
        weeklyTaskUpdateCount: user?.weeklyTaskUpdateCount,
        plan: user?.plan,
        benefit: user?.benefit,
        now: mostRecentDoneAt ?? new Date(),
      });
      setDoneModalContent(modalContent);

      if (isSelfCheck) {
        saveSelfCheckData();
      }
      submit();

      if (isThoughtRecord) {
        thoughtRecordDone();
      } else if (isMeditationRecord) {
        meditationRecordDone();
      } else {
        activityDone();
      }
    }
  };

  useEffect(() => {
    setIsLoading(
      isActivityDoneLoading ||
        isThoughtRecordDoneLoading ||
        isMeditationRecordDoneLoading ||
        isSelfCheckLoading
    );
  }, [
    isActivityDoneLoading,
    isThoughtRecordDoneLoading,
    isMeditationRecordDoneLoading,
    isSelfCheckLoading,
    setIsLoading,
  ]);

  const handleDoneModalAction = () => {
    setModalOpen(false);
    handleGoBack();
  };

  const handleMoveToThoughtRecordAction = () => {
    setMoveToThoughtRecordModalOpen(false);
    openThoughtRecord();
  };

  const handleMoveToMeditationAction = () => {
    setMoveToMeditationModalOpen(false);
    if (isInappWebview) {
      openMeditationRecordDetailPage();
    } else {
      openMeditationRecord();
    }
  };

  const handleUndoneModalAction = () => {
    undoneAction();
    setUndoneModalOpen(false);
  };

  const displayIsDone = isDone || isOptimisticDone;

  const button = (
    <Button
      size={size}
      startDecorator={displayIsDone ? <CheckIconSvgDoneButton /> : undefined}
      disabled={disabled || isLoading}
      loading={isLoading}
      onClick={() => {
        if (displayIsDone) {
          setUndoneModalOpen(true);
        } else {
          doneAction();
        }
      }}
      variant={displayIsDone ? "Outlined" : "Solid"}
      sx={{
        ...(displayIsDone && { "--Button-gap": "4px" }),
      }}
    >
      {displayIsDone ? "마침" : "마치기"}
    </Button>
  );

  return (
    <Box sx={{ position: "relative" }}>
      {button}
      {disabled && !isLoading && (
        <Box
          onClick={highlightUnfilledBlocks}
          sx={{ position: "absolute", inset: 0 }}
        />
      )}
      <CustomModal
        open={isDoneModalOpen}
        onClose={handleDoneModalAction}
        title={doneModalContent.title}
        subtitle={doneModalContent.subtitle}
        actions={[
          {
            label: "확인",
            onClick: handleDoneModalAction,
            variant: "Solid",
          },
        ]}
        footer={
          <Stack direction="row" justifyContent="space-between" spacing={"6px"}>
            <CustomTypography
              variant={"Caption"}
              sx={{
                pr: 0.2,
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
              }}
            >
              업데이트 규칙이 궁금한가요?
            </CustomTypography>
            <CustomTypography
              variant={"Caption"}
              sx={{
                px: 0,
                color: theme.vars.palette.Content.Highlight,
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 700,
                textUnderlineOffset: "2px",
                textDecorationColor: theme.vars.palette.Content.Highlight,
              }}
              onClick={() => {
                setModalOpen(false);
                if (isInappWebview) {
                  navigate("/support-faq?fid=12");
                } else {
                  setUpdatePolicyModalOpen(true);
                }
              }}
            >
              규정 보기
            </CustomTypography>
          </Stack>
        }
      />
      <CustomModal
        open={isMoveToMeditationModalOpen}
        onClose={handleMoveToMeditationAction}
        title={"<명상훈련지>로 이동할게요."}
        subtitle={"그곳에서 활동지를 계속 진행해주세요."}
        actions={[
          {
            label: "확인",
            onClick: handleMoveToMeditationAction,
            variant: "Solid",
          },
        ]}
      />
      <CustomModal
        open={isMoveToThoughtRecordModalOpen}
        onClose={handleMoveToThoughtRecordAction}
        title={"<생각기록지>로 이동할게요."}
        subtitle={"그곳에서 활동지를 계속 진행해주세요."}
        actions={[
          {
            label: "확인",
            onClick: handleMoveToThoughtRecordAction,
            variant: "Solid",
          },
        ]}
      />
      <CustomModal
        open={isUndoneModalOpen}
        onClose={() => {
          setUndoneModalOpen(false);
        }}
        title="활동지를 다시 열까요?"
        subtitle="활동지를 다시 열어 내용을 수정할 수 있어요. 끝나면 꼭 ‘마치기’를 눌러주세요."
        actions={[
          {
            label: "다시 열기",
            onClick: handleUndoneModalAction,
            variant: "Solid",
          },
        ]}
      />
      <CustomModal
        open={isUpdatePolicyModalOpen}
        onClose={() => {
          setUpdatePolicyModalOpen(false);
        }}
        title="활동은 언제 업데이트 받을 수 있나요?"
        subtitle={
          "코치 선생님은 평일 오전 8시부터 오후 7시 사이에 활동 업데이트를 해드리고 있답니다.\n\n" +
          "직접 확인 후 다음 활동을 정성껏 준비하는 작업이라, 정확한 예정 시각을 확정하기 어려운 점 양해 부탁드립니다.\n\n" +
          "[활동 업데이트 규정]\n\n" +
          "플랜별 최대 업데이트 횟수\n" +
          "- 스타터 플랜: 주 최대 2회\n" +
          "- 챌린저 플랜: 일 최대 1회\n" +
          "- 마스터 플랜: 일 최대 2회\n\n" +
          "업데이트 제공 기준\n" +
          "- 오후 3시 이전까지 파란색 원으로 표시된 주어진 활동지가 모두 완료되었다면, 당일 업데이트\n" +
          "- 오후 3시 이후에 파란색 원으로 표시된 주어진 활동지가 모두 완료되었다면, 다음 업무일 업데이트\n\n" +
          "주말과 공휴일은 코치 선생님의 휴무일로 활동 업데이트가 어렵습니다."
        }
        actions={[
          {
            label: "확인",
            onClick: () => setUpdatePolicyModalOpen(false),
            variant: "Solid",
          },
        ]}
      />
    </Box>
  );
}
